/**
 * MSAL wiring for the `entraExternalId` authentication mode.
 *
 * Kept separate from {@link AuthProvider} so the browser-only MSAL types are imported in exactly one
 * place, and so the mapping from Greasemap's {@link AuthConfig} to an MSAL {@link Configuration} is
 * testable on its own.
 */

import type { Configuration } from '@azure/msal-browser';
import type { AuthConfig } from './config';

/**
 * Builds the MSAL browser configuration from the resolved auth settings.
 *
 * `cacheLocation` is `localStorage` rather than the default `sessionStorage` so a token survives the
 * full-page redirect round-trip and a reload — a sign-in that evaporates on refresh reads as broken.
 * `knownAuthorities` lists the CIAM host so MSAL trusts the `*.ciamlogin.com` authority without an
 * extra metadata probe.
 *
 * @param config - The resolved authentication configuration.
 * @returns The MSAL configuration.
 */
export function toMsalConfiguration(config: AuthConfig): Configuration {
  return {
    auth: {
      clientId: config.msal.clientId,
      authority: config.msal.authority,
      knownAuthorities: [new URL(config.msal.authority).host],
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      cacheLocation: 'localStorage',
    },
  };
}

/**
 * The scopes requested when acquiring an access token for the Greasemap API.
 *
 * @param config - The resolved authentication configuration.
 * @returns The scope list containing the API's delegated scope.
 */
export function apiScopes(config: AuthConfig): string[] {
  return [config.msal.apiScope];
}
